// components/artifacts/ArtifactSelectionMenu.js - 텍스트 선택 메뉴 컴포넌트

import React, { useState, useEffect, useRef } from 'react';
import { alertUnlessQuotaExceeded } from '../../services/apiClient';
import './ArtifactSelectionMenu.scss';
import HighlightOverlay from './HighlightOverlay';

/**
 * 텍스트 선택 시 나타나는 플로팅 메뉴 컴포넌트
 * @param {object} props
 * @param {boolean} props.enabled - 메뉴 활성화 여부 (isEditing === false일 때 true)
 * @param {object} props.artifact - 아티팩트 객체 (id, content 포함)
 * @param {function} props.onRequestModification - 수정 요청 콜백 (selectedText, prompt) => void (레거시, 하위 호환성)
 * @param {function} props.onModificationComplete - 수정 완료 콜백 (updatedContent) => void
 * @param {function} props.onDirectEdit - 직접 수정 콜백 (selectedText) => void (에디터 모드 전환용)
 */
const ArtifactSelectionMenu = ({
  enabled,
  artifact,
  onDirectEdit,
  onAiRequest, // (selectedText, userPrompt, selectionPos?, meta?) => Promise<void>
  editor, // TipTap editor instance (optional)
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [mode, setMode] = useState('initial'); // 'initial' | 'ai_input'
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const modeRef = useRef(mode);
  const isVisibleRef = useRef(isVisible);
  const rangeRef = useRef(null);
  const [overlayRects, setOverlayRects] = useState([]);
  const selectionPosRef = useRef(null); // { from, to } (ProseMirror positions)

  const isRangeInsideEl = (range, el) => {
    try {
      if (!range || !el) return false;
      let node = range.commonAncestorContainer;
      if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
      if (!node) return false;
      return el.contains(node);
    } catch (e) {
      return false;
    }
  };

  const isRangeInsideEditor = (range) => {
    try {
      if (!editor?.view?.dom || !range) return false;
      let node = range.commonAncestorContainer;
      if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
      if (!node) return false;
      return editor.view.dom.contains(node);
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  const clearOverlay = () => {
    rangeRef.current = null;
    setOverlayRects([]);
  };

  const computeRectsFromRange = (range) => {
    if (!range) return [];
    try {
      const rectList = Array.from(range.getClientRects());
      // 문서 기준 absolute 좌표로 변환 (viewport rect + scroll)
      const scrollX = window.scrollX || window.pageXOffset || 0;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      return rectList
        .filter((r) => r && r.width > 0 && r.height > 0)
        .map((r) => ({
          top: r.top + scrollY,
          left: r.left + scrollX,
          width: r.width,
          height: r.height,
        }));
    } catch (e) {
      return [];
    }
  };

  const computeProseMirrorSelectionFromDOMRange = (range) => {
    if (!editor || !editor.view || !range) return null;
    try {
      const view = editor.view;
      // range.startContainer/endContainer는 보통 Text 노드
      let from = view.posAtDOM(range.startContainer, range.startOffset);
      let to = view.posAtDOM(range.endContainer, range.endOffset);
      if (typeof from !== "number" || typeof to !== "number") return null;
      if (from === to) return null;
      if (from > to) [from, to] = [to, from];
      return { from, to };
    } catch (e) {
      return null;
    }
  };

  const extractHtmlFromRange = (range) => {
    if (!range) return "";
    try {
      const container = document.createElement("div");
      container.appendChild(range.cloneContents());
      return container.innerHTML || "";
    } catch (e) {
      return "";
    }
  };

  // 이벤트 리스너는 1회만 등록하고(ref로 상태 확인) 레이스를 제거
  useEffect(() => {
    if (!enabled) {
      setIsVisible(false);
      setMode('initial');
      setInputValue('');
      clearOverlay();
      return;
    }

    const getSelectedText = () => (window.getSelection()?.toString() || '').trim();

    const openMenuAtSelection = () => {
      const selection = window.getSelection();
      const text = getSelectedText();
      if (!selection || !text) return;

      try {
        const range = selection.getRangeAt(0);

        // ✅ 메뉴/입력창 내부에서의 selection(Ctrl+A 등)은 무시 (입력값 날아가는 문제 방지)
        if (isRangeInsideEl(range, menuRef.current) || isRangeInsideEl(range, inputRef.current)) {
          return;
        }

        // ✅ 에디터가 있으면, 에디터 내부 selection만 대상으로 삼는다(본문 밖 selection 무시)
        if (editor && !isRangeInsideEditor(range)) {
          return;
        }

        rangeRef.current = range.cloneRange();
        setOverlayRects(computeRectsFromRange(rangeRef.current));
        // 드래그가 살아있는 순간에 ProseMirror selection(from/to)도 함께 저장
        selectionPosRef.current = computeProseMirrorSelectionFromDOMRange(rangeRef.current);
        const rect = range.getBoundingClientRect();

        setPosition({
          // 선택 영역 "아래"에 깔리게
          top: rect.bottom + 8,
          left: rect.left + rect.width / 2,
        });
        setSelectedText(text);
        setIsVisible(true);
        // 새로 드래그하면 항상 기본 메뉴로 복귀
        modeRef.current = 'initial';
        setMode('initial');
        setInputValue('');
      } catch (e) {
        // range가 없는 경우 무시
      }
    };

    const closeMenu = () => {
      setIsVisible(false);
      setMode('initial');
      setInputValue('');
      clearOverlay();
      selectionPosRef.current = null;
    };

    const handleSelectionEnd = () => {
      // ai_input 상태에서도, 사용자가 다른 영역을 드래그하면 그 선택으로 타겟/위치를 업데이트
      setTimeout(() => {
        // ai_input 중 textarea 내부 selection은 무시
        try {
          const sel = window.getSelection?.();
          if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            if (isRangeInsideEl(r, inputRef.current) || isRangeInsideEl(r, menuRef.current)) {
              return;
            }
          }
        } catch (e) {
          // ignore
        }

        const text = getSelectedText();
        if (text) {
          openMenuAtSelection();
        } else {
          // 버튼 클릭 등으로 selection이 풀려도 ai_input이면 메뉴 유지
          if (modeRef.current !== 'ai_input') {
            closeMenu();
          }
        }
      }, 10);
    };

    const handleSelectionChange = () => {
      // ai_input 중에는 본문 selection을 바꿀 때만 반응(입력창 selection은 무시)
      if (modeRef.current === 'ai_input') {
        try {
          const sel = window.getSelection?.();
          if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            if (isRangeInsideEl(r, inputRef.current) || isRangeInsideEl(r, menuRef.current)) {
              return;
            }
          }
        } catch (e) {
          // ignore
        }
      }

      const text = getSelectedText();
      if (text) {
        // 선택이 새로 생기면(ai_input 중이어도) 기본 메뉴로 복귀 + 위치/타겟 업데이트
        openMenuAtSelection();
        return;
      }

      // 선택이 없어졌을 때:
      // - ai_input이면(요약/AI수정 버튼 클릭으로 selection이 풀리는 케이스) 메뉴 유지
      // - 그 외에는 닫기
      if (!text && isVisibleRef.current) {
        if (modeRef.current !== 'ai_input') {
          closeMenu();
        }
      }
    };

    const handleClickOutside = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      if (inputRef.current && inputRef.current.contains(event.target)) return;

      // 바깥 클릭 + 선택 없음 => 닫기
      closeMenu();
    };

    document.addEventListener('mouseup', handleSelectionEnd);
    document.addEventListener('keyup', handleSelectionEnd);
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleSelectionEnd);
      document.removeEventListener('keyup', handleSelectionEnd);
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [enabled]);

  // 스크롤/리사이즈 시 오버레이 위치 보정 (Range가 있으면 다시 계산)
  useEffect(() => {
    if (!enabled) return;
    if (!isVisible) return;
    if (!rangeRef.current) return;

    const update = () => {
      if (!rangeRef.current) return;
      setOverlayRects(computeRectsFromRange(rangeRef.current));

      // 메뉴도 선택 영역을 따라가게(화면 fixed 고정 방지)
      try {
        const rect = rangeRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 8,
          left: rect.left + rect.width / 2,
        });
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [enabled, isVisible]);

  // AI 입력 모드일 때 포커스 및 textarea 자동 높이 조절
  useEffect(() => {
    if (mode === 'ai_input' && inputRef.current) {
      inputRef.current.focus();
      // textarea 자동 높이 조절
      if (inputRef.current.tagName === 'TEXTAREA') {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
      }
    }
  }, [mode]);

  // textarea 내용 변경 시 자동 높이 조절
  useEffect(() => {
    if (inputRef.current && inputRef.current.tagName === 'TEXTAREA') {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
    }
  }, [inputValue]);

  // 메뉴 위치 조정
  useEffect(() => {
    if (isVisible && menuRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedLeft = position.left;
      let adjustedTop = position.top;

      if (menuRect.right > viewportWidth) {
        adjustedLeft = viewportWidth - menuRect.width - 10;
      }
      if (menuRect.left < 0) {
        adjustedLeft = 10;
      }
      if (menuRect.top < 0) {
        adjustedTop = position.top + 60;
      }
      if (menuRect.bottom > viewportHeight) {
        adjustedTop = viewportHeight - menuRect.height - 10;
      }

      if (adjustedLeft !== position.left || adjustedTop !== position.top) {
        setPosition({ top: adjustedTop, left: adjustedLeft });
      }
    }
  }, [isVisible, position]);

  // 직접 수정 버튼 클릭
  const handleDirectEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDirectEdit) {
      onDirectEdit(selectedText, selectionPosRef.current);
    }
    window.getSelection().removeAllRanges();
    setIsVisible(false);
    setMode('initial');
    setInputValue('');
    clearOverlay();
  };

  // AI 요청 버튼 클릭
  const handleAIRequest = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // 클릭 순간 레이스 방지: ref를 먼저 잠금
    modeRef.current = 'ai_input';
    setMode('ai_input');
  };

  // AI 입력 전송
  const handleAISubmit = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setIsLoading(true);

    try {
      if (typeof onAiRequest !== "function") {
        throw new Error("AI 요청 핸들러가 연결되지 않았습니다.");
      }
      await onAiRequest(selectedText, userMessage, selectionPosRef.current, {
        selection_html: extractHtmlFromRange(rangeRef.current),
        overlay_rects: overlayRects,
      });

      // 요청 성공 시 메뉴 닫기 (모달은 부모가 띄움)
      modeRef.current = "initial";
      setIsVisible(false);
      setMode("initial");
      setInputValue("");
      clearOverlay();
      selectionPosRef.current = null;
    } catch (error) {
      console.error("❌ 텍스트 수정 오류:", error);
      alertUnlessQuotaExceeded(error, `텍스트 수정 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim() && !isLoading) {
        handleAISubmit(e);
      }
    } else if (e.key === 'Escape') {
      // 포커스/외부 클릭과 동일하게 닫기
      modeRef.current = 'initial';
      setIsVisible(false);
      setMode('initial');
      setInputValue('');
      clearOverlay();
    }
  };

  // 메뉴 컨테이너 mousedown (capture) - 버튼 클릭 시 포커스 이동으로 selection이 풀리는 걸 최소화
  const handleMenuMouseDownCapture = (e) => {
    if (e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'INPUT') {
      e.preventDefault();
    }
  };

  // "요약/AI수정" 버튼은 mousedown 단계에서 바로 잠금 (selectionchange 레이스 차단)
  const handleLockAiInputOnMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    modeRef.current = 'ai_input';
    setMode('ai_input');
  };

  if (!enabled || !isVisible) {
    return null;
  }

  return (
    <>
      <HighlightOverlay rects={overlayRects} color="rgba(58, 102, 255, 0.18)" />
      <div
        ref={menuRef}
        className={`artifact-selection-menu ${isVisible ? 'visible' : ''} ${mode === 'ai_input' ? 'ai-input-mode' : ''}`}
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
        onMouseDownCapture={handleMenuMouseDownCapture}
      >
      {mode === 'initial' ? (
        <div className="menu-buttons">
          <button
            className="menu-button ai-request-button primary-button"
            onMouseDown={handleLockAiInputOnMouseDown}
            onClick={handleAIRequest}
            title="AI가 전체 맥락을 고려하여 추론하고 더 풍부하게 작성해드립니다"
          >
            AI 수정
          </button>
          <button
            className="menu-button direct-edit-button default-button"
            onClick={handleDirectEdit}
          >
            직접 수정
          </button>
        </div>
      ) : (
        <div className="input-container">
          <div className="input-header">
            <span className="input-label">✨ 어떻게 수정해 드릴까요?</span>
          </div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '-8px', marginBottom: '4px', lineHeight: 1.4 }}>
            AI가 전체 맥락과 요청사항을 고려하여 추론하고 더 풍부하게 작성해드립니다
          </div>
          <div className="input-wrapper">
            <textarea
              ref={inputRef}
              className="modify-input"
              placeholder="예: 더 정중하게, 핵심만 요약, 영어로 번역, 근거를 더 추가하고 구체화해줘"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={isLoading}
              rows={1}
            />
            <button
              className="input-send-button"
              onClick={handleAISubmit}
              disabled={!inputValue.trim() || isLoading}
            >
              {isLoading ? '처리 중...' : '↵'}
            </button>
          </div>
          <div className="input-suggestions">
            <span className="suggestions-label">(추천)</span>
            <button
              className="suggestion-chip"
              onClick={() => setInputValue('근거를 더 추가하고 구체화해줘')}
            >
              근거 추가·구체화
            </button>
            <button
              className="suggestion-chip"
              onClick={() => setInputValue('더 정중하게')}
            >
              더 정중하게
            </button>
            <button
              className="suggestion-chip"
              onClick={() => setInputValue('핵심만 요약')}
            >
              핵심만 요약
            </button>
            <button
              className="suggestion-chip"
              onClick={() => setInputValue('영어로 번역')}
            >
              영어로 번역
            </button>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ArtifactSelectionMenu;
